import React, { useEffect, useImperativeHandle, useState, forwardRef } from 'react';
import { IoMdCheckmark } from "react-icons/io";
import classes from './Stepper.module.css'

interface Step {
  label: string;
  value: number;
}

interface StepperProps {
  steps: Step[];
  activeStep?: number;
  initialCompletedSteps?: number[];
  onStepComplete?: (step: number) => void;
  onStepBack?: (step: number) => void;
  onStepChange?: (step: number) => void;
}

export interface StepperHandles {
  handleNextStep: () => void;
  handlePrevStep: () => void;
}

const Stepper = forwardRef<StepperHandles, StepperProps>(({
  steps,
  activeStep: externalActiveStep,
  initialCompletedSteps = [],
  onStepComplete,
  onStepBack,
  onStepChange,
}, ref) => {
  const [internalActiveStep, setInternalActiveStep] = useState<number>(0);
  const [completedSteps, setCompletedSteps] = useState<number[]>(initialCompletedSteps);

  // Update completed steps when initialCompletedSteps changes
  useEffect(() => {
    setCompletedSteps(initialCompletedSteps);
  }, [initialCompletedSteps]);

  const activeStep = typeof externalActiveStep === 'number' 
    ? externalActiveStep
    : internalActiveStep;

const handleStepClick = (index: number): void => {
  // Update completed steps based on the clicked step
  if (index > activeStep) {
    // Moving forward - mark all steps up to current active step as completed
    const newCompletedSteps = Array.from({length: activeStep + 1}, (_, i) => i);
    setCompletedSteps(newCompletedSteps);
  } else if (index < activeStep) {
    // Moving backward - remove steps after the clicked step from completed steps
    const newCompletedSteps = completedSteps.filter(step => step < index);
    setCompletedSteps(newCompletedSteps);
  }

  if (typeof externalActiveStep === 'undefined') {
    setInternalActiveStep(index);
  }
  onStepChange?.(index);
};

  useImperativeHandle(ref, () => ({
    handleNextStep: () => {
      const nextStep = Math.min(activeStep + 1, steps.length - 1);
      
      // Ensure all previous steps are marked as completed
      const newCompletedSteps = Array.from({length: activeStep + 1}, (_, i) => i);
      setCompletedSteps(newCompletedSteps);

      if (typeof externalActiveStep === 'undefined') {
        setInternalActiveStep(nextStep);
      }
      onStepComplete?.(activeStep);
    },
    handlePrevStep: () => {
      const prevStep = Math.max(activeStep - 1, 0);
      
      // Remove steps after the current step from completed steps
      const newCompletedSteps = completedSteps.filter(step => step < activeStep);
      setCompletedSteps(newCompletedSteps);

      if (typeof externalActiveStep === 'undefined') {
        setInternalActiveStep(prevStep);
      }
      onStepBack?.(activeStep);
    }
  }));

  return (
    <div className={classes.stepper_container}>
      <div className={classes.stepper_wrapper}>
        {steps.map((step, index) => (
          <div key={index} className={classes.step_container}>
            <div className={classes.step_wrapper}>
              <button
                type="button"
                onClick={() => handleStepClick(index)}
                className={`${classes.step_circle} ${completedSteps.includes(index)
                  ? classes.completed
                  : index === activeStep
                    ? classes.active
                    : ''
                  }`}
                aria-label={`Step ${index + 1}: ${step.label}`}
                aria-current={index === activeStep ? 'step' : undefined}
              >
                {completedSteps.includes(index) && (
                  <IoMdCheckmark className={classes.check_icon} />
                )}
              </button>

              {index < steps.length - 1 && (
                <div
                  className={`${classes.step_line} ${(completedSteps.includes(index) || activeStep > index)
                      ? classes.completedLine
                      : classes.upcomingLine
                    }`}
                  aria-hidden="true"
                />
              )}
            </div>

            <span className={classes.step_label}>
              {step.label}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
});

export default Stepper;